// Game-flow finite state machine. Replaces v1's parallel boolean flags
// (gameRunning / isGameOver / isPaused / manuallyPaused) — this is the only
// place game state is written.

export type GameState =
  'boot' | 'menu' | 'running' | 'paused' | 'combat' | 'game-over' | 'shop' | 'daily-intro';

const TRANSITIONS: Record<GameState, readonly GameState[]> = {
  boot: ['menu'],
  menu: ['running', 'shop', 'daily-intro'],
  running: ['paused', 'combat', 'game-over'],
  paused: ['running', 'menu'],
  combat: ['running', 'game-over'],
  'game-over': ['running', 'menu'],
  shop: ['menu'],
  'daily-intro': ['running', 'menu'],
};

export interface StateHooks {
  enter?: (from: GameState) => void;
  exit?: (to: GameState) => void;
}

export class GameFsm {
  private current: GameState = 'boot';
  private readonly hooks = new Map<GameState, StateHooks>();

  get state(): GameState {
    return this.current;
  }

  is(state: GameState): boolean {
    return this.current === state;
  }

  onState(state: GameState, hooks: StateHooks): void {
    this.hooks.set(state, hooks);
  }

  canTransition(to: GameState): boolean {
    return TRANSITIONS[this.current].includes(to);
  }

  transition(to: GameState): void {
    if (!this.canTransition(to)) {
      throw new Error(`Illegal state transition: ${this.current} -> ${to}`);
    }
    const from = this.current;
    this.hooks.get(from)?.exit?.(to);
    this.current = to;
    this.hooks.get(to)?.enter?.(from);
  }
}
